import { PropsWithChildren } from "react";

import { SafeAreaView, View } from "react-native";
import {
  IconButton,
  MD3TypescaleKey,
  Text,
  useTheme,
} from "react-native-paper";
import { useTailwind } from "tailwind-rn";

type Props = Partial<
  PropsWithChildren<{
    title: string;
    disableBorder: boolean;
    variant: keyof typeof MD3TypescaleKey;

    /** optional padding to show when `onBackPress` is undefined. Ignored if `onBackPress` is defined */
    padLeft: boolean;
    onBackPress: () => void;

    /** children aligned and bound with `title` text by default.
     * can optionally bind to edges, and therefore give children a 'full' width
     */
    childrenHorizontalAlignMode: "edges" | "title";

    /** `rightAction` takes precedence over `onClosePress` */
    rightAction: React.ReactNode;
    /** if `rightAction` is defined, `onClosePress` is ignored */
    onClosePress: () => void;
  }>
>;

export default function Header({
  title,
  children,
  padLeft = false,
  disableBorder = false,
  childrenHorizontalAlignMode = "title",
  variant = "headlineMedium",
  rightAction = null,
  onBackPress,
  onClosePress,
}: Props) {
  const tailwind = useTailwind();
  const { colors } = useTheme();

  const iconColor = colors.onSurface;

  const borderStyle = [
    tailwind("border-b"),
    { borderColor: colors.surfaceDisabled },
  ];

  const viewStyle = [
    { backgroundColor: colors.surface },
    disableBorder ? {} : borderStyle,
  ];

  return (
    <SafeAreaView style={viewStyle}>
      <View style={tailwind("flex-row justify-between")}>
        {onBackPress ? (
          <IconButton
            icon="arrow-left"
            iconColor={iconColor}
            onPress={onBackPress}
          />
        ) : (
          <View style={tailwind("pl-3")}>
            {padLeft && <View style={tailwind("w-10")} />}
          </View>
        )}

        <View style={tailwind("flex-1")}>
          {title && (
            <Text
              variant={variant}
              style={tailwind("font-bold leading-10 py-2")}
              numberOfLines={2}
            >
              {title}
            </Text>
          )}

          {childrenHorizontalAlignMode === "title" && children}
        </View>

        {onClosePress || rightAction ? (
          rightAction || (
            <IconButton
              icon="close"
              iconColor={iconColor}
              onPress={onClosePress}
            />
          )
        ) : (
          <View style={tailwind("pr-3")} />
        )}
      </View>

      {childrenHorizontalAlignMode === "edges" && children}
    </SafeAreaView>
  );
}
